import { useState, useEffect, useRef } from 'react'
import { FaBars, FaTimes, FaPhoneAlt, FaClock, FaArrowRight } from 'react-icons/fa'
import { scrollToSection } from '../utils/scroll'
import { useNavigate, useLocation } from 'react-router-dom'
import GlassSurface from './GlassSurface'

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [glassPos, setGlassPos] = useState({ x: 50, y: 50 })
  const [isOverLightBackground, setIsOverLightBackground] = useState(false)
  const navBarRef = useRef(null)
  const navItemRefs = useRef([])
  const isNavigatingRef = useRef(false)

  // Function to check if background is light
  const checkBackgroundColor = () => {
    if (!navBarRef.current) return
    
    const navRect = navBarRef.current.getBoundingClientRect()
    // Check a point just below the navbar (about 10px below)
    const checkY = navRect.bottom + 10
    const centerX = navRect.left + navRect.width / 2
    
    // Get element directly below the navbar
    const elementBelow = document.elementFromPoint(centerX, checkY)
    
    if (!elementBelow) {
      setIsOverLightBackground(false)
      return
    }
    
    // Walk up the DOM tree to find the actual background element
    let currentElement = elementBelow
    let backgroundColor = null
    
    // Skip the navbar itself if we hit it
    while (currentElement && currentElement !== document.body) {
      // Skip if we're still in the navbar
      if (currentElement.closest('nav')) {
        currentElement = currentElement.parentElement
        continue
      }
      
      const styles = window.getComputedStyle(currentElement)
      const bgColor = styles.backgroundColor
      
      // Check for white/light background classes first
      const classList = currentElement.classList
      if (classList) {
        const hasWhiteBg = Array.from(classList).some(cls => 
          cls.includes('bg-white') || 
          cls.includes('bg-gray-50') || 
          cls.includes('bg-gray-100') ||
          cls.includes('bg-gray-200') ||
          cls.includes('bg-slate-50') ||
          cls.includes('bg-slate-100')
        )
        if (hasWhiteBg) {
          setIsOverLightBackground(true)
          return
        }
      }
      
      // Check if background is not transparent
      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        backgroundColor = bgColor
        break
      }
      
      currentElement = currentElement.parentElement
    }
    
    // Parse RGB and check if it's light
    if (backgroundColor) {
      const rgbMatch = backgroundColor.match(/\d+/g)
      if (rgbMatch && rgbMatch.length >= 3) {
        const r = parseInt(rgbMatch[0])
        const g = parseInt(rgbMatch[1])
        const b = parseInt(rgbMatch[2])
        // Calculate luminance (perceived brightness)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
        setIsOverLightBackground(luminance > 0.5)
      } else {
        setIsOverLightBackground(false)
      }
    } else {
      setIsOverLightBackground(false)
    }
  }

  useEffect(() => {
    // Update active section based on current route
    if (location.pathname === '/careers') {
      setActiveSection('careers')
    } else if (location.pathname === '/') {
      // Only track scroll on home page
      const handleScroll = () => {
        setScrolled(window.scrollY > 20)
        
        // Skip scroll-driven updates during programmatic navigation
        if (isNavigatingRef.current) return
        
        // Update active section based on scroll position
        const sections = ['home', 'specialities', 'testimonials', 'about', 'contact']
        const scrollPosition = window.scrollY + 100
        
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i])
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(sections[i])
            break
          }
        }
        
        // Check background color on scroll
        checkBackgroundColor()
      }
      
      // Initial check
      checkBackgroundColor()
      
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', checkBackgroundColor)
      return () => {
        window.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', checkBackgroundColor)
      }
    }
  }, [location.pathname])

  // Ensure glass aligns to active section on load/scroll/resize
  useEffect(() => {
    const onResize = () => updateGlassToSection(activeSection)
    updateGlassToSection(activeSection)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  const updateGlassToSection = (sectionId) => {
    if (!navBarRef.current) return
    const idx = navLinks.findIndex((l) => l.id === sectionId)
    const target = navItemRefs.current[idx]
    if (target) {
      const parentRect = navBarRef.current.getBoundingClientRect()
      const rect = target.getBoundingClientRect()
      const center = ((rect.left + rect.width / 2 - parentRect.left) / parentRect.width) * 100
      setGlassPos({ x: Math.min(Math.max(center, 8), 92), y: 50 })
    }
  }

  const handleNavClick = (sectionId) => {
    // Prevent scroll handler from interfering during navigation
    isNavigatingRef.current = true
    
    // If clicking Careers, navigate to careers page
    if (sectionId === 'careers') {
      navigate('/careers')
      setIsOpen(false)
      return
    }
    
    // If we're not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/')
      // Wait for navigation, then scroll
      setTimeout(() => {
        setActiveSection(sectionId)
        updateGlassToSection(sectionId)
        scrollToSection(sectionId)
      }, 100)
    } else {
      setActiveSection(sectionId)
      updateGlassToSection(sectionId)
      scrollToSection(sectionId)
    }
    
    setIsOpen(false)
    
    // Re-enable scroll updates after scroll animation completes
    setTimeout(() => {
      isNavigatingRef.current = false
    }, 1000)
  }

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'specialities', label: 'Specialities' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'about', label: 'About Us' },
    { id: 'contact', label: 'Contact' },
    { id: 'careers', label: 'Careers' },
  ]

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent pointer-events-auto">
      {/* Top bar with contact + hours (dummy data) */}
      <div className="bg-gradient-to-r from-dark-900/90 via-dark-800/90 to-dark-900/90 text-white text-sm border-b border-dark-700/30 backdrop-blur-md">
        <div className="container-custom py-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <FaPhoneAlt className="text-accent-400" />
              <a href="tel:+13318082721" className="font-semibold tracking-tight hover:text-accent-300 transition-colors text-xs sm:text-sm">
                +1 331 808 2721
              </a>
            </div>
            <span className="rounded-full bg-gradient-to-r from-dark-700 to-dark-600 px-2 sm:px-3 py-1 font-semibold border border-dark-500/30 text-xs sm:text-sm whitespace-nowrap">24/7 Dispatch</span>
            <div className="hidden sm:flex items-center gap-2">
              <FaClock className="text-accent-400" />
              <span>Mon–Fri: 8:00am – 6:00pm</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3 text-sm">
            <a href="mailto:info@ewlogisticsusa.com" className="text-gray-300 hover:text-white transition-colors">
              info@ewlogisticsusa.com
            </a>
          </div>
        </div>
      </div>

      <div className="container-custom px-3 sm:px-0">
        {/* Mobile Navbar - GlassSurface effect but no capsule shape */}
        <div className="md:hidden relative mt-3 mb-2">
          <GlassSurface
            width="100%"
            height={80}
            borderRadius={16}
            blur={9}
            displace={0.4}
            distortionScale={-120}
            redOffset={-3}
            greenOffset={9}
            blueOffset={16}
            backgroundOpacity={0.06}
            saturation={1.2}
            opacity={0.9}
            mixBlendMode="screen"
            className="relative"
          >
          <div
            ref={navBarRef}
            className={`relative flex items-center justify-between h-16 px-4 gap-2 transition-all w-full min-w-0 ${
              isOverLightBackground ? 'text-black' : 'text-white'
            }`}
          >
            {/* Logo */}
            <button
              onClick={() => {
                if (location.pathname !== '/') {
                  navigate('/')
                } else {
                  handleNavClick('home')
                }
              }}
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer relative z-10 flex-shrink-0 min-w-0 max-w-[calc(100%-60px)]"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl overflow-hidden flex items-center justify-center bg-white/8 border border-white/15 p-1 flex-shrink-0">
                <img
                  src="/logo.png"
                  alt="EW Logistics"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <span className={`text-lg sm:text-2xl font-bold whitespace-nowrap truncate ${
                isOverLightBackground ? 'text-black' : 'text-white'
              }`}>
                EW Logistics
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              className={`focus:outline-none relative z-50 flex-shrink-0 w-10 h-10 flex items-center justify-center ${
                isOverLightBackground ? 'text-black' : 'text-white'
              }`}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <FaBars size={24} />
            </button>
          </div>

          </GlassSurface>
        </div>

        {/* Mobile Sidebar Overlay - slides in from left like vawcom.com */}
        {isOpen && (
          <>
            {/* Backdrop overlay */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
              onClick={() => setIsOpen(false)}
            />
            {/* Sidebar menu */}
            <div className="fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-gradient-to-br from-dark-900/95 via-dark-800/95 to-dark-900/95 backdrop-blur-xl z-50 md:hidden shadow-2xl transform transition-transform duration-300 ease-out border-r border-white/10">
              <div className="flex flex-col h-full">
                {/* Sidebar header with logo and close button */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                  <span className="text-2xl font-bold text-white">EW Logistics</span>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-white hover:text-accent-400 transition-colors p-2"
                    aria-label="Close menu"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>

                {/* Menu items */}
                <div className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
                  {navLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => {
                        handleNavClick(link.id)
                        setIsOpen(false)
                      }}
                      className={`block w-full text-left py-4 px-4 rounded-lg transition-all duration-200 ${
                        (activeSection === link.id || (link.id === 'careers' && location.pathname === '/careers'))
                          ? 'bg-accent-400/20 text-accent-400 font-semibold'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {link.label}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      handleNavClick('contact')
                      setIsOpen(false)
                    }}
                    className="block w-full btn-primary text-center mt-4"
                  >
                    <span className="flex items-center justify-center">
                      Get Quote
                      <FaArrowRight className="ml-2" size={14} />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Desktop Navbar - With GlassSurface capsule */}
        <div className="hidden md:block">
          <GlassSurface
            width="100%"
            height={scrolled ? 72 : 80}
            borderRadius={999}
            blur={9}
            displace={0.4}
            distortionScale={-120}
            redOffset={-3}
            greenOffset={9}
            blueOffset={16}
            backgroundOpacity={scrolled ? 0.1 : 0.06}
            saturation={1.2}
            opacity={0.9}
            mixBlendMode="screen"
            className="relative mt-3 mb-2 sm:mt-4"
          >
            <div
              ref={navBarRef}
              className={`relative flex items-center justify-between h-16 sm:h-18 px-4 sm:px-6 lg:px-8 gap-4 transition-all w-full min-w-0 ${
                isOverLightBackground ? 'text-black' : 'text-white'
              }`}
            >
              {/* Floating liquid glass overlay (mouse-follow) */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ zIndex: 0 }}>
                <div
                  className="absolute transition-all duration-300 ease-out"
                  style={{
                    left: `${glassPos.x}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <GlassSurface
                    width={90}
                    height={40}
                    borderRadius={999}
                    blur={8}
                    displace={0.55}
                    distortionScale={-95}
                    redOffset={-3}
                    greenOffset={8}
                    blueOffset={14}
                    backgroundOpacity={0.14}
                    saturation={1.18}
                    opacity={0.88}
                    mixBlendMode="screen"
                    className="shadow-lg"
                  />
                </div>
              </div>

              {/* Logo */}
              <button
                onClick={() => {
                  if (location.pathname !== '/') {
                    navigate('/')
                  } else {
                    handleNavClick('home')
                  }
                }}
                className="flex items-center space-x-3 cursor-pointer relative z-10 flex-shrink-0 min-w-0"
              >
                <div className="w-12 h-12 rounded-2xl overflow-hidden flex items-center justify-center bg-white/8 border border-white/15 p-1 flex-shrink-0">
                  <img
                    src="/logo.png"
                    alt="EW Logistics"
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <span className={`text-2xl font-bold whitespace-nowrap ${
                  isOverLightBackground ? 'text-black' : 'text-white'
                }`}>
                  EW Logistics
                </span>
              </button>

              {/* Desktop Navigation */}
              <div className="flex items-center space-x-6 lg:space-x-8 xl:space-x-10 relative z-10 flex-1 justify-end">
                <div className="flex items-center space-x-6 lg:space-x-8 xl:space-x-10">
                  {navLinks.map((link, idx) => (
                    <button
                      key={link.id}
                      ref={(el) => (navItemRefs.current[idx] = el)}
                      onClick={() => handleNavClick(link.id)}
                      className={`font-medium transition-colors duration-200 whitespace-nowrap px-2 py-1 ${
                        (activeSection === link.id || (link.id === 'careers' && location.pathname === '/careers'))
                          ? `${isOverLightBackground ? 'text-black' : 'text-white'}`
                          : isOverLightBackground ? 'text-black/70 hover:text-black' : 'text-white/70 hover:text-white'
                      }`}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleNavClick('contact')}
                  className="btn-primary text-sm ml-8 xl:ml-10 flex-shrink-0"
                >
                  <span className="flex items-center whitespace-nowrap">
                    Get Quote
                    <FaArrowRight className="ml-2" size={14} />
                  </span>
                </button>
              </div>
            </div>
          </GlassSurface>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
